import { useCallback, useLayoutEffect, RefObject } from 'react';

import { IUserNameBadgeSource } from 'APP/constants/userBadges';
import { useAnimateSticker } from 'APP/packages/stickerAnimation/useAnimateSticker';
import Entities from 'STORE';

interface IPresenterProps {
  animationUrl?: string;
  animationData?: IUserNameBadgeSource;
  autoplay?: boolean;
  displayUILayer?: number;
  inView: boolean;
  loop?: boolean;
}

interface IUseAnimationPresenter {
  stickerContainerRef: RefObject<HTMLSpanElement>;
}

export const useAnimationPresenter = (props: IPresenterProps): IUseAnimationPresenter => {
  const {
    animationUrl,
    displayUILayer,
    autoplay,
    animationData = undefined,
    loop = true,
    inView,
  } = props;

  const onLoopComplete = useCallback(() => {
    if (!loop && animationRef.current) {
      animationRef.current.pause();
    }
  }, [loop]);

  const { stickerContainerRef, animationRef } = useAnimateSticker<HTMLSpanElement>({
    autoplay,
    url: animationUrl,
    animationData,
    onLoopComplete,
  });

  const onChangeInView = useCallback(
    (isInViewport: boolean) => {
      if (!animationRef.current?.isLoaded) {
        return;
      }

      if (isInViewport) {
        if (Entities.App.isFocused && displayUILayer === Entities.App.uiLayer) {
          animationRef.current.play();
        }
      } else {
        animationRef.current.pause();
      }
    },
    [displayUILayer]
  );

  useLayoutEffect(() => {
    onChangeInView(inView);
    animationRef.current?.updateIsVisible(inView);
  }, [inView, onChangeInView]);

  useLayoutEffect(() => {
    if (Entities.App.isFocused && displayUILayer === Entities.App.uiLayer && inView) {
      animationRef.current?.play();
    } else {
      animationRef.current?.pause();
    }
  }, [inView, displayUILayer, Entities.App.isFocused]);

  return {
    stickerContainerRef,
  };
};
